export type QuotationStatus = "pending" | "paid" | "expired" | "cancelled";

export function deriveQuotationStatus(
  state: string | undefined,
  validityDate: string | null | undefined,
): QuotationStatus {
  const s = (state ?? "").toLowerCase();
  if (s === "sale" || s === "done") return "paid";
  if (s === "cancel") return "cancelled";

  if (validityDate) {
    const validity = new Date(validityDate);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    validity.setHours(0, 0, 0, 0);
    if (validity < now) return "expired";
  }
  return "pending";
}

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

export interface ExpiryInfo {
  label: string | null;
  isUrgent: boolean;
  isExpired: boolean;
  /** Progress of remaining validity, 0 (just expired) to 1 (full window). */
  progress: number;
}

/**
 * Build a human-readable countdown to validityDate.
 * createDate is used to size the progress bar; falls back to a 7-day window.
 */
export function getExpiryInfo(
  validityDate: string | null | undefined,
  createDate?: string | null,
): ExpiryInfo {
  if (!validityDate) {
    return { label: null, isUrgent: false, isExpired: true, progress: 0 };
  }

  const validity = new Date(validityDate).getTime();
  const now = Date.now();
  const remaining = validity - now;

  if (remaining <= 0) {
    return { label: null, isUrgent: false, isExpired: true, progress: 0 };
  }

  const created = createDate ? new Date(createDate).getTime() : validity - 7 * DAY_MS;
  const window = Math.max(validity - created, DAY_MS);
  const progress = Math.max(0, Math.min(1, remaining / window));

  const days = Math.floor(remaining / DAY_MS);
  const hours = Math.floor((remaining % DAY_MS) / HOUR_MS);

  let label: string;
  if (days >= 1) {
    label = hours > 0 ? `${days} days ${hours} hrs` : `${days} days`;
  } else {
    label = `${hours} hrs`;
  }

  const isUrgent = remaining < DAY_MS;

  return { label, isUrgent, isExpired: false, progress };
}

export function formatCreatedAt(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `CREATED ${date
    .toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    .toUpperCase()}`;
}
